import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  FileText, Plus, Trash2, Printer, CheckCircle2, Clock,
  AlertCircle, X, Save, Globe, Users, RefreshCw, ArrowRight,
  Shield, BookOpen, Tag, Calendar, Info, Eye, History,
  Database, WifiOff, Wifi, CheckSquare, Workflow, FlaskConical,
  Lock, Unlock, AlertTriangle as AlertTriangleIcon,
} from 'lucide-react';
import {
  useProposal, type Proposal, type ProposalStatus, type SyncStatus,
  buildProposalNumber, buildLocalNumber, buildProposalBaseNumber,
  COUNTRY_STANDARD, incrementRevision,
} from '@/context/ProposalContext';
import { useSession } from '@/context/SessionContext';
import { apiSaveProposal, apiUpdateProposal, apiDeleteProposal } from '@/lib/phpApi';
import { useAuth } from '@/hooks/useAuth';
import { getRetentionPolicy, isWithinRetention, retentionToastMessage, adminOverridePrompt } from '@/lib/retention';
import PageHeader from '@/components/PageHeader';

// ─── Country info ─────────────────────────────────────────────────────────────
const COUNTRY_FLAGS: Record<string, string> = {
  AU: '🇦🇺', GB: '🇬🇧', UK_EA: '🇬🇧', US: '🇺🇸',
  CA: '🇨🇦', NZ: '🇳🇿', IE: '🇮🇪', DE: '🇩🇪',
  FR: '🇫🇷', SG: '🇸🇬', OTHER: '🌐', INT: '🌐',
};
const COUNTRY_NAMES: Record<string, string> = {
  AU: 'Australia', GB: 'United Kingdom', UK_EA: 'United Kingdom', US: 'United States',
  CA: 'Canada', NZ: 'New Zealand', IE: 'Ireland', DE: 'Germany',
  FR: 'France', SG: 'Singapore', OTHER: 'International', INT: 'International',
};

function countryLabel(code?: string) {
  if (!code) return '';
  return `${COUNTRY_FLAGS[code] || '🌐'} ${COUNTRY_NAMES[code] || code}`;
}

// ─── Sync dot ─────────────────────────────────────────────────────────────────
function SyncDot({ status, isLocal }: { status: SyncStatus; isLocal: boolean }) {
  if (isLocal && status !== 'synced') {
    return (
      <span title="Local only — not in database" className="flex flex-col items-center gap-0.5">
        <span className="w-3 h-3 rounded-full bg-rose-500" />
        <span className="text-[10px] text-rose-400 font-bold leading-none">LOCAL</span>
      </span>
    );
  }
  if (status === 'pending') {
    return <span title="Sync in progress" className="w-3 h-3 rounded-full bg-amber-400 animate-pulse" />;
  }
  if (status === 'failed') {
    return <span title="Sync failed" className="w-3 h-3 rounded-full bg-rose-500" />;
  }
  // synced
  return (
    <span title="Saved to database" className="w-3 h-3 rounded-full bg-emerald-500 shadow-emerald-500/50 shadow-[0_0_5px_2px]" />
  );
}

// ─── Printable proposal ───────────────────────────────────────────────────────
function PrintableProposal({ proposal }: { proposal: Proposal }) {
  const isLocal = proposal.isLocalOnly && !proposal.savedToDb;
  return (
    <div className="hidden print:block p-10 text-black font-sans" id="print-proposal">
      {/* Watermark for local drafts */}
      {isLocal && (
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%) rotate(-45deg)', fontSize: 72, color: 'rgba(0,0,0,0.07)', fontWeight: 900, whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 0 }}>
          DRAFT — LOCAL ONLY
        </div>
      )}
      {/* Cover */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div className="border-b-4 border-gray-800 pb-6 mb-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 style={{ fontSize: 28, fontWeight: 900, margin: 0 }}>Reynolds &amp; Bauhm</h1>
              <p style={{ fontSize: 12, color: '#666', margin: '4px 0 0' }}>Smart Wastewater Assessment Tool</p>
              <p style={{ fontSize: 11, color: '#999', margin: '2px 0 0' }}>Water Treatment Process Train Platform</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 700, margin: 0 }}>
                {proposal.proposalNumber}
              </p>
              <p style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                {proposal.status.toUpperCase()} · Rev {proposal.revisionNumber}
              </p>
              <p style={{ fontSize: 10, color: '#999' }}>
                {new Date(proposal.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}
              </p>
              {proposal.countryCode && (
                <p style={{ fontSize: 10, color: '#666', marginTop: 4 }}>
                  Standard: {COUNTRY_STANDARD[proposal.countryCode] || 'ISO'}
                </p>
              )}
            </div>
          </div>
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>{proposal.title}</h2>
        {proposal.description && <p style={{ fontSize: 13, color: '#444', marginTop: 8 }}>{proposal.description}</p>}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 24, fontSize: 12 }}>
          <tbody>
            {[
              ['Customer', proposal.customerName || '—'],
              ['Proposal Number', proposal.proposalNumber],
              ['Revision', `R${proposal.revisionNumber}`],
              ['Status', proposal.status],
              ['Country Standard', proposal.countryCode ? (COUNTRY_STANDARD[proposal.countryCode] || '—') : '—'],
              ['Date', new Date(proposal.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })],
            ].map(([label, value]) => (
              <tr key={label} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '6px 8px', fontWeight: 600, width: '30%', color: '#555' }}>{label}</td>
                <td style={{ padding: '6px 8px' }}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {proposal.equipmentList && proposal.equipmentList.length > 0 && (
          <div style={{ marginTop: 32 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, borderBottom: '1px solid #ddd', paddingBottom: 4 }}>Process Train Equipment</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12, alignItems: 'center', fontSize: 12 }}>
              {proposal.equipmentList.map((eq: any, i: number) => (
                <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <strong>{eq.label}</strong>
                  {i < (proposal.equipmentList?.length ?? 0) - 1 && <span style={{ color: '#999' }}>→</span>}
                </span>
              ))}
            </div>
          </div>
        )}
        {proposal.notes && (
          <div style={{ marginTop: 32 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, borderBottom: '1px solid #ddd', paddingBottom: 4 }}>Notes &amp; Assumptions</h3>
            <p style={{ fontSize: 12, color: '#444', marginTop: 8, whiteSpace: 'pre-wrap' }}>{proposal.notes}</p>
          </div>
        )}
        <div style={{ marginTop: 48, borderTop: '1px solid #ddd', paddingTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32 }}>
            <div>
              <div style={{ borderTop: '1px solid #000', width: 200, paddingTop: 4, fontSize: 11, color: '#555' }}>Prepared by</div>
            </div>
            <div>
              <div style={{ borderTop: '1px solid #000', width: 200, paddingTop: 4, fontSize: 11, color: '#555' }}>Approved by</div>
            </div>
          </div>
        </div>
        <p style={{ fontSize: 9, color: '#aaa', marginTop: 24 }}>© {new Date().getFullYear()} Reynolds &amp; Bauhm. Proprietary &amp; Confidential. Preliminary assessment only — not for final engineering design.</p>
      </div>
    </div>
  );
}

// ─── Create / Revision Modal ──────────────────────────────────────────────────
interface CreateModalProps {
  existingProposal?: Proposal;
  isRevision?: boolean;
  onClose: () => void;
  onSaved: (p: Proposal) => void;
}

function CreateProposalModal({ existingProposal, isRevision, onClose, onSaved }: CreateModalProps) {
  const { currentCustomer, userProfile } = useSession();
  const { saveProposal, updateProposal, previewProposalNumber, consumeProposalNumber } = useProposal();

  // Country code is locked to the active session customer's country first,
  // then the user profile country, then INT (international) as fallback.
  // Proposal numbering prefix must match the regulatory jurisdiction of the customer.
  const countryCode = (currentCustomer?.country as string | undefined)
    || (userProfile?.country as string | undefined)
    || 'INT';
  const standardLabel = COUNTRY_STANDARD[countryCode] || 'ISO';

  const [title, setTitle]           = useState(existingProposal?.title || '');
  const [description, setDesc]      = useState(existingProposal?.description || '');
  const [notes, setNotes]           = useState(existingProposal?.notes || '');
  const [revisionReason, setReason] = useState('');
  const [isLocalOnly, setLocal]     = useState(existingProposal?.isLocalOnly ?? false);
  const [saving, setSaving]         = useState(false);

  // Live preview of proposal number
  const previewNumber = isRevision && existingProposal
    ? incrementRevision(existingProposal)
    : previewProposalNumber(countryCode, isLocalOnly);

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    if (isRevision && !revisionReason.trim()) { toast.error('Revision reason is required'); return; }
    setSaving(true);

    let proposalNumber: string;
    let baseNumber: string;
    let revisionNumber: number;

    if (isRevision && existingProposal) {
      proposalNumber = incrementRevision(existingProposal);
      baseNumber     = existingProposal.baseNumber;
      revisionNumber = existingProposal.revisionNumber + 1;
    } else if (isLocalOnly) {
      const localNum = buildLocalNumber();
      proposalNumber = localNum;
      baseNumber     = localNum;
      revisionNumber = 0;
    } else {
      const fullNum  = consumeProposalNumber(countryCode);
      const base     = buildProposalBaseNumber(countryCode, 0); // base extracted from full
      // Re-derive base from full number
      proposalNumber = fullNum;
      baseNumber     = fullNum.replace(/-R\d+$/, '').replace(/\/R\d+$/, '').replace(/-REV\d+$/, '');
      revisionNumber = 0;
    }

    const data: Omit<Proposal, 'id' | 'createdAt' | 'updatedAt'> = {
      proposalNumber,
      baseNumber,
      revisionNumber,
      title: title.trim(),
      description: description.trim() || undefined,
      notes: notes.trim() || undefined,
      revisionReason: isRevision ? revisionReason.trim() : undefined,
      customerId: currentCustomer?.id || null,
      customerName: currentCustomer?.company || currentCustomer?.name || null,
      status: 'draft',
      isLocalOnly,
      isImmutable: false,
      savedToDb: false,
      syncStatus: 'pending',
      countryCode,
      standard: standardLabel,
      equipmentList: existingProposal?.equipmentList,
      processTrainIds: isRevision ? existingProposal?.processTrainIds : undefined,
      sampleIds: isRevision ? existingProposal?.sampleIds : undefined,
    };

    const saved = saveProposal(data);

    if (isLocalOnly) {
      toast.info('Local-only proposal saved. Samples in this session will NOT sync to database.', { duration: 6000 });
    } else {
      // Try DB sync
      try {
        // Use serverId (DB integer) for the API call — never the local timestamp ID
        const dbCustId = currentCustomer
          ? ((currentCustomer as any).serverId
              ?? (currentCustomer.id.length < 13 && Number(currentCustomer.id) > 0 ? Number(currentCustomer.id) : null))
          : null;
        const result = await apiSaveProposal({
          ...data,
          customerId: dbCustId,
        });
        if (result.success && result.id) {
          updateProposal(saved.id, { savedToDb: true, dbId: result.id, syncStatus: 'synced' });
          toast.success(`Proposal ${proposalNumber} saved to database`);
        }
      } catch {
        updateProposal(saved.id, { syncStatus: 'failed' });
        toast.warning('Saved locally — database sync pending');
      }
    }

    setSaving(false);
    onSaved(saved);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 pt-12 overflow-y-auto">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg overflow-hidden rb-section">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800/50 bg-gradient-to-r from-violet-500/10 to-transparent">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-violet-400" />
            <div>
              <h2 className="text-sm font-bold text-slate-100">{isRevision ? 'Create Revision' : 'New Proposal'}</h2>
              <p className="text-[10px] text-slate-500">{standardLabel} standard · {countryLabel(countryCode)}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800/50"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Customer */}
          {currentCustomer ? (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-500/5 border border-cyan-500/20">
              <Users className="w-3.5 h-3.5 text-cyan-400" />
              <span className="text-[11px] text-cyan-300 font-medium">{currentCustomer.company || currentCustomer.name}</span>
              <span className="ml-auto text-[10px] text-cyan-500">{countryLabel(countryCode)}</span>
            </div>
          ) : (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
              <AlertCircle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-[10px] text-amber-400">No customer selected. Select a customer first for proper traceability and numbering.</p>
            </div>
          )}

          {/* Proposal number preview */}
          <div className="space-y-1">
            <label className="text-[10px] font-medium text-slate-400">Proposal Number (auto-generated)</label>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-violet-500/5 border border-violet-500/20">
              <Tag className="w-3.5 h-3.5 text-violet-400 shrink-0" />
              <span className={`text-sm font-bold font-mono ${isLocalOnly ? 'text-amber-300' : 'text-violet-300'}`}>
                {isLocalOnly ? 'LOCAL-XXXXX (temporary)' : previewNumber}
              </span>
            </div>
            <p className="text-[10px] text-slate-600">
              {isLocalOnly ? 'Assigned on local save · Replaced with official number when synced to DB'
                : `${standardLabel} · unique 5-digit sequential · immutable once saved`}
            </p>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-medium text-slate-400">Title *</label>
            <input value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Dairy Wastewater Treatment — Preliminary Assessment"
              className="w-full px-3 py-2.5 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-violet-500/40" />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-medium text-slate-400">Description</label>
            <textarea value={description} onChange={e => setDesc(e.target.value)}
              placeholder="Scope, objectives, and background..." rows={2}
              className="w-full px-3 py-2.5 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-violet-500/40 resize-none" />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-medium text-slate-400">Notes &amp; Assumptions</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Assumptions, limitations, regulatory notes..." rows={2}
              className="w-full px-3 py-2.5 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-violet-500/40 resize-none" />
          </div>

          {/* Revision reason — mandatory when isRevision */}
          {isRevision && (
            <div className="space-y-1">
              <label className="text-[10px] font-medium text-slate-400 flex items-center gap-1">
                <AlertTriangleIcon className="w-3 h-3 text-amber-400" />
                Revision Reason <span className="text-rose-400 ml-0.5">*</span>
              </label>
              <textarea
                value={revisionReason}
                onChange={e => setReason(e.target.value)}
                placeholder="Describe what changed and why this revision was required…"
                rows={2}
                className={`w-full px-3 py-2.5 rounded-lg bg-slate-800/50 border text-sm text-slate-200 placeholder-slate-600 focus:outline-none resize-none ${
                  !revisionReason.trim() ? 'border-amber-500/40 focus:border-amber-500/60' : 'border-slate-800/50 focus:border-violet-500/40'
                }`}
              />
              {!revisionReason.trim() && (
                <p className="text-[10px] text-amber-400">Required — validated proposals require a documented revision reason for auditability.</p>
              )}
            </div>
          )}

          {/* Local-only option */}
          {!isRevision && (
            <div onClick={() => setLocal(v => !v)}
              className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                isLocalOnly ? 'bg-amber-500/10 border-amber-500/30' : 'bg-slate-800/30 border-slate-800/50 hover:border-slate-600'
              }`}>
              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all ${isLocalOnly ? 'bg-amber-500 border-amber-500' : 'border-slate-600'}`}>
                {isLocalOnly && <CheckSquare className="w-3 h-3 text-white" />}
              </div>
              <div>
                <p className={`text-[11px] font-bold ${isLocalOnly ? 'text-amber-400' : 'text-slate-300'}`}>
                  Local Draft Only — Do Not Save to Database
                </p>
                <p className="text-[10px] text-slate-500 mt-0.5">Use for preliminary scoping that does not need a permanent record. Can be promoted to DB-saved at any time.</p>
              </div>
            </div>
          )}

          {isLocalOnly && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/5 border border-amber-500/30">
              <Info className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-[10px] text-amber-300">
                <strong>Important:</strong> Samples created while in Local Draft Mode will be stored in your browser only and will be lost if browser data is cleared. They will NOT be synced to the database.
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-700 text-slate-400 text-xs font-bold hover:bg-slate-800/50">Cancel</button>
            <button onClick={handleSave} disabled={saving || !title.trim()}
              className="flex-1 py-2.5 rounded-xl bg-violet-500/20 text-violet-400 border border-violet-500/30 text-xs font-bold hover:bg-violet-500/30 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              <Save className="w-3.5 h-3.5" />{saving ? 'Saving...' : isRevision ? 'Create Revision' : 'Save Proposal'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ProposalPage() {
  const { proposals, activeProposal, enterProposalMode, exitProposalMode, deleteProposal, updateProposal, setSyncStatus, logActivity } = useProposal();
  const { currentCustomer } = useSession();
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab]       = useState<ProposalStatus | 'all'>('all');
  const [showCreate, setShowCreate]     = useState(false);
  const [revisionTarget, setRevisionTarget] = useState<Proposal | null>(null);
  const [printTarget, setPrintTarget]   = useState<Proposal | null>(null);
  const [syncingId, setSyncingId]       = useState<string | null>(null);

  useEffect(() => {
    logActivity('page_visit', 'proposals', currentCustomer?.id, activeProposal?.id);
  }, []); // eslint-disable-line

  const [showAllProposals, setShowAllProposals] = useState(false);

  // Customer-scoped proposals
  const customerProposals = currentCustomer
    ? proposals.filter(p => p.customerId === currentCustomer.id || p.customerId === String(parseInt(currentCustomer.id)))
    : proposals;
  const baseProposals = (currentCustomer && !showAllProposals) ? customerProposals : proposals;

  const filtered = baseProposals.filter(p => activeTab === 'all' || p.status === activeTab);
  const draftCount     = baseProposals.filter(p => p.status === 'draft').length;
  const validatedCount = baseProposals.filter(p => p.status === 'validated').length;

  const handleDelete = (p: Proposal) => {
    if (p.isImmutable) {
      toast.error(`Proposal ${p.proposalNumber} is validated and immutable. Create a revision to make changes.`);
      return;
    }
    // Determine the applicable standard from the proposal's country code
    const standard = COUNTRY_STANDARD[p.countryCode ?? ''] || p.countryCode || 'UK_EA';
    const policy = getRetentionPolicy(standard);
    if (isWithinRetention(p.createdAt, policy)) {
      if (!isAdmin) {
        toast.error(retentionToastMessage(policy, p.createdAt));
        return;
      }
      if (!window.confirm(adminOverridePrompt(policy, p.createdAt, `proposal ${p.proposalNumber}`))) return;
      deleteProposal(p.id);
      if (p.savedToDb && p.dbId) apiDeleteProposal(p.dbId).catch(() => {});
      if (activeProposal?.id === p.id) exitProposalMode();
      toast.warning(`Proposal deleted (admin override — within ${policy.retentionYears}-year retention period)`);
      return;
    }
    if (!confirm(`Delete proposal ${p.proposalNumber}?\nThis cannot be undone.`)) return;
    deleteProposal(p.id);
    if (p.savedToDb && p.dbId) apiDeleteProposal(p.dbId).catch(() => {});
    if (activeProposal?.id === p.id) exitProposalMode();
    toast.success('Proposal deleted');
  };

  const handleValidate = (p: Proposal) => {
    updateProposal(p.id, { status: 'validated', isImmutable: true });
    toast.success(`Proposal ${p.proposalNumber} validated & locked — create a revision to modify`);
    logActivity('proposal_validated', 'proposals', currentCustomer?.id, p.id);
  };

  const handleSyncToDb = async (p: Proposal) => {
    setSyncingId(p.id);
    setSyncStatus(p.id, 'pending');
    try {
      const result = await apiSaveProposal({
        proposalNumber: p.proposalNumber,
        baseNumber: p.baseNumber,
        revisionNumber: p.revisionNumber,
        title: p.title, description: p.description, notes: p.notes,
        status: p.status, countryCode: p.countryCode,
        customerId: currentCustomer?.id ? parseInt(currentCustomer.id) : null,
        isLocalOnly: false,
      });
      if (result.success) {
        updateProposal(p.id, { savedToDb: true, dbId: result.id, syncStatus: 'synced', isLocalOnly: false });
        toast.success('Synced to database');
      }
    } catch {
      setSyncStatus(p.id, 'failed');
      toast.error('Database sync failed');
    }
    setSyncingId(null);
  };

  const handlePrint = (p: Proposal) => {
    setPrintTarget(p);
    setTimeout(() => { window.print(); }, 200);
  };

  return (
    <div className="max-w-[1440px] mx-auto space-y-5">
      {/* Hidden print target */}
      {printTarget && <PrintableProposal proposal={printTarget} />}

      <PageHeader
        icon={BookOpen}
        iconColor="text-violet-400"
        title="Proposals"
        subtitle={`Country-traceable numbering · draft → validated lifecycle · revision management${currentCustomer ? ` · ${currentCustomer.company || currentCustomer.name}` : ''}`}
        badge={activeProposal ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-violet-500/10 border border-violet-500/20 text-violet-400 text-[10px] font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
            Active: {activeProposal.proposalNumber}
          </span>
        ) : undefined}
        actions={
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-500/20 text-violet-400 border border-violet-500/30 hover:bg-violet-500/30 text-xs font-bold transition-all">
            <Plus className="w-4 h-4" />New Proposal
          </button>
        }
      />

      {/* Active customer banner */}
      {currentCustomer && (
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-cyan-500/20 bg-cyan-500/5">
          <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse shrink-0" />
          <Users className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
          <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider">Filtered for:</span>
          <span className="text-sm font-bold text-white">{currentCustomer.company}</span>
          {(currentCustomer as any).standard && (
            <span className="text-[10px] px-1.5 py-0.5 rounded border border-cyan-500/20 bg-cyan-500/10 text-cyan-400 font-bold">
              {(currentCustomer as any).standard.replace('_', ' ')}
            </span>
          )}
          <span className="text-[10px] text-slate-500 ml-1">
            {customerProposals.length} proposal{customerProposals.length !== 1 ? 's' : ''}
          </span>
          <button onClick={() => setShowAllProposals(v => !v)}
            className="ml-auto text-[10px] text-slate-500 hover:text-slate-300 px-2 py-1 rounded-lg hover:bg-slate-800/50 transition-all">
            {showAllProposals ? 'Mine only' : 'Show all'}
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total', value: proposals.length, color: 'text-violet-400', bg: 'bg-violet-500/10', border: 'border-violet-500/20' },
          { label: 'Draft', value: draftCount, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
          { label: 'Validated', value: validatedCount, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} border ${s.border} rounded-xl p-4 flex items-center gap-4`}>
            <span className={`text-3xl font-black ${s.color}`}>{s.value}</span>
            <div>
              <p className={`text-[11px] font-bold ${s.color}`}>{s.label}</p>
              <p className="text-[10px] text-slate-600 mt-0.5">proposals</p>
            </div>
          </div>
        ))}
      </div>

      {/* Numbering standard reference */}
      <div className="bg-[#0d1b30] border border-violet-500/15 rounded-xl p-4">
        <p className="text-[10px] font-bold text-violet-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <Globe className="w-3.5 h-3.5" /> Country Numbering Standards in Use
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[10px]">
          {[
            { flag: '🇦🇺', fmt: 'AU-PRO-2026-00001-R0', std: 'WSAA/AWA' },
            { flag: '🇬🇧', fmt: 'UK-PROP/2026/00001/R0', std: 'UKWIR/WIMES' },
            { flag: '🇺🇸', fmt: 'US-AWWA-2026-00001-REV0', std: 'AWWA' },
            { flag: '🌐', fmt: 'INT-PRO-2026-00001-R0', std: 'ISO' },
          ].map(r => (
            <div key={r.std} className="bg-slate-800/30 rounded-lg p-2 space-y-0.5">
              <p className="font-bold text-slate-300">{r.flag} {r.std}</p>
              <p className="font-mono text-violet-300/80 break-all">{r.fmt}</p>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-slate-600 mt-2">R0/REV0 = original · R1/REV1 = first revision · NNNNN = 5-digit global sequential</p>
      </div>

      {/* Proposals list */}
      <div className="overflow-hidden rb-inner-card">
        <div className="flex items-center border-b border-slate-800/60 px-4 pt-3 gap-1">
          {(['all', 'draft', 'validated'] as const).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-xs font-bold rounded-t-lg border-b-2 transition-all capitalize ${
                activeTab === tab
                  ? tab === 'all' ? 'border-violet-500 text-violet-400' : tab === 'draft' ? 'border-amber-500 text-amber-400' : 'border-emerald-500 text-emerald-400'
                  : 'border-transparent text-slate-500 hover:text-slate-300'
              }`}>
              {tab === 'all' ? `All (${proposals.length})` : tab === 'draft' ? `Draft (${draftCount})` : `Validated (${validatedCount})`}
            </button>
          ))}
        </div>

        <div className="p-4">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <BookOpen className="w-10 h-10 mx-auto mb-3 text-slate-400" />
              <p className="text-sm font-medium">No proposals yet</p>
              <p className="text-[11px] mt-1">Click New Proposal to start tracking your project work</p>
              <button onClick={() => setShowCreate(true)}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-500/20 text-violet-400 border border-violet-500/30 text-xs font-bold hover:bg-violet-500/30">
                <Plus className="w-3.5 h-3.5" /> Create First Proposal
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map(p => {
                const isActive    = activeProposal?.id === p.id;
                const isSyncing   = syncingId === p.id;
                const syncStatusEff: SyncStatus = isSyncing ? 'pending' : (p.syncStatus || 'pending');

                return (
                  <motion.div key={p.id} layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className={`rounded-xl border p-4 transition-all ${
                      isActive ? 'border-violet-500/40 bg-violet-500/5 shadow-[0_0_16px_rgba(139,92,246,0.08)]'
                               : 'border-slate-800 bg-rb-surface hover:border-slate-700'
                    }`}>
                    <div className="flex items-start gap-3">
                      {/* Sync status dot column */}
                      <div className="flex flex-col items-center gap-1 pt-1.5 shrink-0">
                        <SyncDot status={syncStatusEff} isLocal={p.isLocalOnly && !p.savedToDb} />
                      </div>

                      <div className="flex-1 min-w-0 space-y-2">
                        {/* Title row */}
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* Proposal number */}
                              <span className={`font-mono text-xs font-bold ${p.isLocalOnly && !p.savedToDb ? 'text-amber-300' : 'text-violet-300'}`}>
                                {p.proposalNumber}
                              </span>
                              {/* Status badge */}
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border flex items-center gap-1 ${
                                p.status === 'validated' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                                                         : 'bg-amber-500/10 border-amber-500/20 text-amber-400'
                              }`}>
                                {p.isImmutable && <Lock className="w-2.5 h-2.5" />}
                                {p.status}
                              </span>
                              {/* Active mode badge */}
                              {isActive && (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-violet-500/20 border border-violet-500/30 text-violet-400 animate-pulse">
                                  Active Mode
                                </span>
                              )}
                              {/* Local badge */}
                              {p.isLocalOnly && !p.savedToDb && (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/10 border border-amber-500/30 text-amber-400">
                                  LOCAL DRAFT
                                </span>
                              )}
                            </div>
                            <h3 className="text-sm font-bold text-slate-200 mt-1 truncate">{p.title}</h3>
                            {p.description && <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-1">{p.description}</p>}
                          </div>
                        </div>

                        {/* Metadata */}
                        <div className="flex items-center gap-3 flex-wrap text-[10px] text-slate-500">
                          {p.customerName && <span className="flex items-center gap-1"><Users className="w-3 h-3" />{p.customerName}</span>}
                          {p.countryCode && (
                            <span className="flex items-center gap-1">
                              <Globe className="w-3 h-3" />
                              {countryLabel(p.countryCode)} · {COUNTRY_STANDARD[p.countryCode] || 'ISO'}
                            </span>
                          )}
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(p.createdAt).toLocaleDateString()}</span>
                          <span className={`flex items-center gap-1.5 ${
                            syncStatusEff === 'synced' ? 'text-emerald-500' : syncStatusEff === 'pending' ? 'text-amber-400' : 'text-rose-400'
                          }`}>
                            {syncStatusEff === 'synced' ? <Wifi className="w-3 h-3" /> : syncStatusEff === 'pending' ? <RefreshCw className="w-3 h-3 animate-spin" /> : <WifiOff className="w-3 h-3" />}
                            {syncStatusEff === 'synced' ? 'Saved to DB' : syncStatusEff === 'pending' ? 'Sync pending' : 'Sync failed'}
                          </span>
                          <span className="text-slate-600">Rev {p.revisionNumber}</span>
                          {(p.processTrainIds?.length ?? 0) > 0 && (
                            <span className="flex items-center gap-1 text-amber-400">
                              <Workflow className="w-3 h-3" />
                              {p.processTrainIds!.length} train{p.processTrainIds!.length !== 1 ? 's' : ''}
                            </span>
                          )}
                          {(p.sampleIds?.length ?? 0) > 0 && (
                            <span className="flex items-center gap-1 text-sky-400">
                              <FlaskConical className="w-3 h-3" />
                              {p.sampleIds!.length} sample{p.sampleIds!.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 flex-wrap pt-1">
                          {/* Immutable lock banner */}
                          {p.isImmutable && (
                            <div className="w-full flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/5 border border-emerald-500/20 text-[10px] text-emerald-400">
                              <Lock className="w-3 h-3 shrink-0" />
                              Validated &amp; immutable — create a revision to modify
                              {p.revisionReason && <span className="text-slate-500 ml-1">· {p.revisionReason.slice(0, 60)}{p.revisionReason.length > 60 ? '…' : ''}</span>}
                            </div>
                          )}

                          {isActive ? (
                            <button onClick={exitProposalMode}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-violet-500/20 text-violet-400 border border-violet-500/30 hover:bg-violet-500/30">
                              <X className="w-3 h-3" /> Exit Proposal Mode
                            </button>
                          ) : (
                            <button onClick={() => enterProposalMode(p)}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-violet-500/10 text-violet-300 border border-violet-500/20 hover:bg-violet-500/20">
                              <Eye className="w-3 h-3" /> Enter Proposal Mode
                            </button>
                          )}
                          <button
                            onClick={() => setRevisionTarget(p)}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-sky-500/10 text-sky-400 border border-sky-500/20 hover:bg-sky-500/20"
                            title={p.isImmutable ? 'Validated — create revision to make changes' : 'Create a revision'}
                          >
                            <History className="w-3 h-3" /> Revise
                          </button>
                          {p.status === 'draft' && !p.isImmutable && (
                            <button onClick={() => handleValidate(p)}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20">
                              <Shield className="w-3 h-3" /> Validate
                            </button>
                          )}
                          {(!p.savedToDb || p.syncStatus === 'failed') && (
                            <button onClick={() => handleSyncToDb(p)} disabled={isSyncing}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-slate-700/50 text-slate-300 border border-slate-700 hover:bg-slate-700 disabled:opacity-50">
                              {isSyncing ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Database className="w-3 h-3" />}
                              {isSyncing ? 'Syncing…' : 'Sync to DB'}
                            </button>
                          )}
                          <button onClick={() => handlePrint(p)}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-slate-700/50 text-slate-300 border border-slate-700 hover:bg-slate-700">
                            <Printer className="w-3 h-3" /> Print / PDF
                          </button>
                          <button
                            onClick={() => handleDelete(p)}
                            disabled={p.isImmutable}
                            title={p.isImmutable ? 'Cannot delete validated proposal — create a revision instead' : 'Delete proposal'}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-rose-500/10 text-rose-400 border border-rose-500/20 hover:bg-rose-500/20 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            {p.isImmutable ? <Lock className="w-3 h-3" /> : <Trash2 className="w-3 h-3" />}
                            {p.isImmutable ? 'Locked' : 'Delete'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showCreate && <CreateProposalModal onClose={() => setShowCreate(false)} onSaved={() => {}} />}
      {revisionTarget && <CreateProposalModal existingProposal={revisionTarget} isRevision onClose={() => setRevisionTarget(null)} onSaved={() => {}} />}
    </div>
  );
}
